import { noop, queryOptions, useQuery } from "@tanstack/react-query";
import { type StaticDecode, Type } from "typebox";
import * as TypeCompiler from "typebox/compile";
import { Nullable, ReadonlyObject } from "@/shared/schema/schema.ts";
import { httpChecked } from "@/shared/util/fetch.ts";
import { queryClient } from "@/shared/util/queryClient.tsx";

const transformEpoch = Nullable(
  Type.Codec(Type.Number())
    .Decode((value) => new Date(value))
    .Encode((value) => value.getTime()),
);

const schema = ReadonlyObject({
  enabled: Type.Boolean(),
  notice: Type.Codec(Nullable(Type.String()))
    .Decode((value) => value ?? "")
    .Encode((value) => value),
  updatedAt: transformEpoch,
  updatedBy: Nullable(Type.String()),
  companyName: Type.String(),
  mustAgree: Type.Boolean(),
  agreedAt: transformEpoch,
});

const checker = TypeCompiler.Compile(schema);
export type PrivacyNotice = StaticDecode<typeof schema>;

const url = "/privacy-notice";
const queries = {
  fetch: queryOptions({
    queryKey: ["privacy-notice"] as const,
    queryFn: ({ signal }) => httpChecked.get(url, checker, { signal }),
  }),
};

export function usePrivacyNotice() {
  const { data } = useQuery({
    ...queries.fetch,
    // we cache the response forever, but let's refetch every 10 minutes just in
    // case it changes
    refetchInterval: 60_000 * 10,
    staleTime: Infinity,
  });

  return data;
}

export async function prefetchPrivacyNotice() {
  await queryClient
    .query({ ...queries.fetch, staleTime: Infinity })
    // eslint-disable-next-line unicorn/prefer-await
    .catch(noop);
}
